import cn from 'classnames';
import { titleize } from 'inflected';
import { ComponentType, ReactNode } from 'react';
import { Event, EventType, Importance } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Badge } from '@Components/Badge';
import { Appearance, Design, Size, StretchMode } from '@Components/designConstants';
import { AppFieldOf, DateField, SingleDropdownField } from '@Components/Forms/FormField';
import { formatDayMonth } from '@src/utils/date-utils';
import { getImportanceDisplay } from '../activity-feed-utils';
import {
  CHANNEL_OPTIONS,
  EVENT_TYPE_OPTIONS,
  IMPORTANCE_OPTIONS
} from '../Filters/activity-feed-filters-constants';
import { getEventTheme } from '../oracle-taxonomy-utils';
import {
  isDateEditable,
  isImportanceEditable,
  shouldShowChannel
} from './activity-feed-detail-utils';
import { EventEditDraft, EventFormEditor } from './useEditEventForm';

interface DetailInfoCardProps {
  event: Event;
  showWip: boolean;
  editor?: EventFormEditor;
}

export const DetailInfoCard = ({ event, showWip, editor }: DetailInfoCardProps): ReactNode => {
  const wipEditor = showWip ? editor : undefined;
  return (
    <aside className='activity-feed-detail-info-card'>
      <EditableInfoRow
        label='Date'
        editor={isDateEditable(event) ? wipEditor : undefined}
        display={formatDayMonth(event.date)}
        name='date'
        field={DateField}
        fieldProps={{
          appearance: Appearance.OUTLINED,
          design: Design.NEUTRAL,
          stretch: StretchMode.FILL
        }}
      />
      <EditableInfoRow
        label='Event Theme'
        editLabel='Event Type'
        editor={editor}
        display={
          <div className='activity-feed-detail-info-theme'>
            <Badge size={Size.SM} design={Design.NEUTRAL}>
              {getEventTheme(event.eventType)}
            </Badge>
            {event.eventType !== 'Other' && (
              <p className='activity-feed-detail-info-subtheme'>{event.eventType}</p>
            )}
          </div>
        }
        name='eventType'
        field={SingleDropdownField<EventType>}
        fieldProps={{
          ariaLabel: 'Event Type',
          searchable: true,
          options: EVENT_TYPE_OPTIONS,
          placeholder: 'Select event type',
          size: Size.MD
        }}
      />
      <EditableInfoRow
        label='Channel'
        editor={editor}
        display={shouldShowChannel(event, showWip) ? event.channel : undefined}
        name='channel'
        field={SingleDropdownField<string>}
        fieldProps={{
          ariaLabel: 'Channel',
          searchable: true,
          clearable: true,
          options: CHANNEL_OPTIONS,
          placeholder: 'No channel',
          size: Size.MD
        }}
      />
      <EditableInfoRow
        label='Priority'
        editor={wipEditor}
        disabled={!isImportanceEditable(event)}
        display={getImportanceDisplay(event.importance).label}
        name='importance'
        field={SingleDropdownField<Importance>}
        fieldProps={{ ariaLabel: 'Priority', options: IMPORTANCE_OPTIONS }}
      />
      {event.confidenceBand && (
        <InfoRow label='AI Confidence'>{titleize(event.confidenceBand)}</InfoRow>
      )}
    </aside>
  );
};

interface InfoRowProps {
  label: string;
  editable?: boolean;
  children: ReactNode;
}

const InfoRow = ({ label, editable = false, children }: InfoRowProps): ReactNode => (
  <div className='activity-feed-detail-info-row'>
    <p className={cn('activity-feed-detail-info-label', { editable })}>{label}</p>
    <div className='activity-feed-detail-info-value'>{children}</div>
  </div>
);

interface InfoFieldProps {
  disabled?: boolean;
  reserveFeedbackSpace?: boolean;
}

interface EditableInfoRowProps<P extends object> {
  label: string;
  editLabel?: string;
  editor?: EventFormEditor;
  // null or undefined hides the row in read mode.
  display: ReactNode;
  // Shows the field disabled and keeps the label neutral.
  disabled?: boolean;
  name: keyof EventEditDraft;
  field: ComponentType<P & InfoFieldProps>;
  fieldProps: NoInfer<P>;
}

const EditableInfoRow = <P extends object>({
  label,
  editLabel = label,
  editor,
  display,
  disabled = false,
  name,
  field,
  fieldProps
}: EditableInfoRowProps<P>): ReactNode => {
  if (!editor) {
    if (display == null) return undefined;
    return <InfoRow label={label}>{display}</InfoRow>;
  }
  return (
    <InfoRow label={editLabel} editable={!disabled}>
      <AppFieldOf
        form={editor.form}
        name={name}
        field={field}
        fieldProps={{
          ...fieldProps,
          reserveFeedbackSpace: false,
          disabled: disabled || editor.disabled
        }}
      />
    </InfoRow>
  );
};
